<template>
    <div class="space-y-6">
        <h2 class="text-heading-md">Text Input Examples</h2>

        <div class="grid grid-cols-2 gap-4">
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Basic States</h3>
                <div class="space-y-4">
                    <DsTextInput
                        v-model="inputTextValue"
                        helper-text=""
                        label="Full Name"
                        type="text"
                    />

                    <DsTextInput
                        v-model="inputTextValue"
                        helper-text="With helper text"
                        label="Full Name"
                        type="text"
                    />

                    <DsTextInput
                        v-model="inputTextValue"
                        helper-text="Required field"
                        label="Full Name"
                        required
                        type="text"
                    />

                    <DsTextInput
                        v-model="inputTextValue"
                        disabled
                        helper-text="Disabled field"
                        label="Full Name"
                        type="text"
                    />

                    <DsTextInput
                        v-model="inputTextValue"
                        helper-text="Readonly field"
                        label="Full Name"
                        readonly
                        type="text"
                    />
                </div>
            </div>

            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Error States & Slots</h3>
                <div class="space-y-4">
                    <DsTextInput
                        v-model="inputTextValue"
                        error="Error message"
                        helper-text="Field with error"
                        label="Full Name"
                        type="text"
                    />

                    <DsTextInput
                        v-model="inputTextValue"
                        :error="true"
                        helper-text="Field with error"
                        label="Full Name"
                        type="text"
                    >
                        <template #error>A slotted error message</template>
                    </DsTextInput>

                    <DsTextInput
                        v-model="inputTextValue"
                        helper-text="Input with prefix and suffix slots"
                        label="Full Name"
                        type="text"
                    >
                        <template #leading>
                            <DsIcon
                                icon="star"
                                size="md"
                            />
                        </template>
                        <template #trailing>
                            <div class="flex gap-1">
                                <DsIcon
                                    icon="star"
                                    size="md"
                                />
                            </div>
                        </template>
                    </DsTextInput>
                </div>
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Different Input Types</h3>
            <div class="grid grid-cols-3 gap-4">
                <DsTextInput
                    v-model="emailValue"
                    helper-text="Enter your email"
                    label="Email Address"
                    type="email"
                />

                <DsTextInput
                    v-model="passwordValue"
                    helper-text="Enter your password"
                    label="Password"
                    type="password"
                />

                <DsTextInput
                    v-model="numberValue"
                    helper-text="Enter your age"
                    label="Age"
                    type="number"
                />
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">With Mask</h3>
            <div class="grid grid-cols-3 gap-4">
                <DsTextInputMasked
                    v-model="ssnValue"
                    helper-text="Enter your social security number"
                    label="Social security number"
                    :mask="MaskType.SSN"
                    :placeholder="MaskPlaceholder.SSN"
                    type="text"
                />

                <DsTextInputMasked
                    v-model="einValue"
                    helper-text="Enter your EIN"
                    label="EIN"
                    :mask="MaskType.EIN"
                    :placeholder="MaskPlaceholder.EIN"
                    type="text"
                />

                <DsTextInputMasked
                    v-model="accountValue"
                    helper-text="Enter your account number"
                    label="Account Number"
                    :mask="MaskType.ACCOUNT_NUMBER"
                    type="text"
                />
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Masked Input Error States</h3>
            <div class="grid grid-cols-3 gap-4">
                <DsTextInputMasked
                    v-model="ssnValue"
                    error="Social security number is required"
                    helper-text="Enter your social security number"
                    label="Social security number"
                    :mask="MaskType.SSN"
                    :placeholder="MaskPlaceholder.SSN"
                    required
                    type="text"
                />

                <DsTextInputMasked
                    v-model="accountValue"
                    error="Account number is required"
                    helper-text="Enter your account number"
                    label="Account Number"
                    :mask="MaskType.ACCOUNT_NUMBER"
                    required
                    type="text"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import DsTextInputMasked from '@ds/form/input/text/DsTextInputMasked.vue';
import { MaskPlaceholder, MaskType } from '@ds/form/input/text/text-input.types';
import { ref } from 'vue';

const inputTextValue = ref('Text sample');
const emailValue = ref('user@example.com');
const passwordValue = ref('password123');
const numberValue = ref(25);
const ssnValue = ref('');
const einValue = ref('');
const accountValue = ref('');
</script>
